import { LIVE_NEED, MET_NEED } from '@features/needs/infinite/queries'
import { getSeedValue } from '@lib/Auth/clerk-refresh'
import { createQueryKeys, inferQueryKeys } from '@lukemorales/query-key-factory'
import { Needs_Bool_Exp, Needs_Order_By, Order_By } from '@purposity/sdk'
import { sdk } from '@services/sdk'
import { QUANTITY as DEFAULT_PAGE_QUANTITY } from '../infinite/constants'

type QueryKeysFactory = ReturnType<typeof createQueryKeys>

export const needKeysNext: QueryKeysFactory = createQueryKeys('needs', {
  fallback: () => ({
    queryKey: [true],
    async queryFn(ctx) {
      const seedValue = getSeedValue()
      const localParam = {
        where: {
          _and: [
            { private: { _neq: true } },
            { urgent: { _eq: false } },
            LIVE_NEED,
          ],
        },
        order_by: {
          line_number: Order_By.Asc,
        },
      }

      return sdk.new_needs_list(
        {
          seed: seedValue,
          first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
          ...localParam,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),

  user: (userId: number) => ({
    queryKey: [userId],
    async queryFn(ctx) {
      const seedValue = getSeedValue()
      const localParam = {
        where: {
          met_by: {
            id: { _eq: userId },
          },
        },
        order_by: {
          completed_at: Order_By.Desc,
        },
      }

      return sdk.new_needs_list(
        {
          seed: seedValue,
          first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
          ...localParam,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),

  urgent: () => ({
    queryKey: [true],
    async queryFn(ctx) {
      const seedValue = Math.random()
      const localParam = {
        where: {
          _and: [
            { private: { _neq: true } },
            { urgent: { _eq: true } },
            LIVE_NEED,
          ],
        },
        order_by: {
          line_number: Order_By.Asc,
        },
      }

      return sdk.new_needs_list(
        {
          seed: seedValue,
          first: 3,
          ...localParam,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),

  community: (listIds: number[]) => ({
    queryKey: [{ listIds }],
    async queryFn(ctx) {
      const seedValue = getSeedValue()
      const localParam = {
        where: {
          _and: [{ list_id: { _in: listIds } }, LIVE_NEED],
        },
        order_by: [{ line_number: Order_By.Asc }],
      }

      return sdk.new_needs_list(
        {
          seed: seedValue,
          first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
          ...localParam,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),

  gifts: (where: Needs_Bool_Exp[], order_by: Needs_Order_By) => ({
    queryKey: [where, order_by],
    async queryFn(ctx) {
      const seedValue = getSeedValue()
      return sdk.new_needs_list(
        {
          where: {
            _and: [
              {
                title: {
                  _like: `%🎁%`,
                },
              },
              ...where,
            ],
          },
          order_by,
          seed: seedValue,
          first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),

  team: ({ aliasOrValias }: { aliasOrValias?: string }) => {
    const valias = aliasOrValias?.toLowerCase().trim()
    return {
      queryKey: [valias || ''],
      contextQueries: {
        // live: {
        //   queryKey: [true] as const,
        //   async queryFn(ctx) {
        //     const localParam = {
        //       where: {
        //         _and: [
        //           {
        //             organization: {
        //               v_alias: { _eq: valias },
        //             },
        //           },
        //           LIVE_NEED,
        //         ],
        //       },
        //       order_by: {
        //         updated_at: Order_By.Desc,
        //       },
        //     }

        //     return sdk.needsList(
        //       {
        //         first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
        //         ...localParam,
        //         ...ctx.pageParam,
        //       },
        //       ctx.meta?.headers
        //     )
        //   },
        // },
        met: {
          queryKey: [
            MET_NEED,
            Order_By.DescNullsLast,
            Order_By.Desc,
            DEFAULT_PAGE_QUANTITY,
          ] as const,
          async queryFn(ctx) {
            const seedValue = getSeedValue()
            const valias = aliasOrValias?.toLowerCase().trim()
            const localParam = {
              where: {
                _and: [
                  {
                    team_needs: {
                      team: {
                        v_alias: { _eq: valias },
                      },
                    },
                  },
                  MET_NEED,
                ],
              },
              order_by: [
                {
                  completed_at: Order_By.DescNullsLast,
                },
                {
                  updated_at: Order_By.Desc,
                },
              ],
            }

            return sdk.new_needs_list(
              {
                seed: seedValue,
                first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
                ...localParam,
                ...ctx.pageParam,
              },
              ctx.meta?.headers
            )
          },
        },
      },
    }
  },
  org: ({ aliasOrValias }: { aliasOrValias?: string }) => {
    const valias = aliasOrValias?.toLowerCase().trim()
    return {
      queryKey: [valias || ''],
      contextQueries: {
        live: {
          queryKey: [true] as const,
          async queryFn(ctx) {
            const seedValue = getSeedValue()
            const localParam = {
              where: {
                _and: [
                  {
                    organization: {
                      v_alias: { _eq: valias },
                    },
                  },
                  LIVE_NEED,
                ],
              },
              order_by: {
                line_number: Order_By.Asc,
              },
            }

            return sdk.new_needs_list(
              {
                seed: seedValue,
                first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
                ...localParam,
                ...ctx.pageParam,
              },
              ctx.meta?.headers
            )
          },
        },
        met: {
          queryKey: [true] as const,
          async queryFn(ctx) {
            const valias = aliasOrValias?.toLowerCase().trim()
            const localParam = {
              where: {
                _and: [
                  {
                    organization: {
                      v_alias: { _eq: valias },
                    },
                  },
                  MET_NEED,
                ],
              },
              order_by: [
                {
                  completed_at: Order_By.DescNullsLast,
                },
                {
                  updated_at: Order_By.Desc,
                },
              ],
            }

            return sdk.new_needs_list(
              {
                seed: 0,
                first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
                ...localParam,
                ...ctx.pageParam,
              },
              ctx.meta?.headers
            )
          },
        },
      },
    }
  },

  orgs: (orgIds: number[]) => ({
    queryKey: [{ orgIds }],
    async queryFn(ctx) {
      const seedValue = getSeedValue()
      const localParam = {
        where: {
          _and: [{ organization_id: { _in: orgIds } }, LIVE_NEED],
        },
        order_by: [{ id: Order_By.Asc }],
      }

      return sdk.new_needs_list(
        {
          seed: seedValue,
          first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
          ...localParam,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),

  orgTags: (orgQueryData: string[] = [], needQueryData: string[] = []) => ({
    queryKey: [{ org_tags: orgQueryData, need_tags: needQueryData }],
    async queryFn(ctx) {
      const conditions = []

      const seedValue = getSeedValue()

      if (needQueryData.length > 0) {
        conditions.push({
          need_tags: {
            tag: { _in: needQueryData },
          },
        })
      }

      if (orgQueryData.length > 0) {
        conditions.push({
          organization: {
            organization_tags: {
              tag: { _in: orgQueryData },
            },
          },
        })
      }

      const localParam = {
        where: {
          _and: [
            ...(conditions.length > 0 ? [{ _or: conditions }] : []),
            LIVE_NEED,
          ],
        },
        order_by: {
          line_number: Order_By.Asc,
        },
      }

      return sdk.new_needs_list(
        {
          seed: seedValue,
          first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
          ...localParam,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),
})
export type NeedKeysNext = inferQueryKeys<typeof needKeysNext>

export const needKeysGeo: QueryKeysFactory = createQueryKeys('needsGeo', {
  point: (latitude: number, longitude: number, need_tags?: string[]) => ({
    queryKey: [{ latitude, longitude, need_tags }],
    async queryFn(ctx) {
      const hasTags = need_tags && need_tags?.length > 0
      const seedValue = getSeedValue()
      let whereConditional:
        | typeof LIVE_NEED
        | { _or: Array<{ data?: any } | typeof LIVE_NEED> } = {
        _or: [
          {
            data: {
              _contains: { tags: need_tags },
            },
            ...LIVE_NEED,
          },
          LIVE_NEED,
        ],
      }

      if (!hasTags) whereConditional = LIVE_NEED

      const localParam = {
        geo: {
          latitude,
          longitude,
          seed: seedValue,
        },
        where: whereConditional,
        order_by: [
          {
            line_number: Order_By.Asc,
          },
        ],
      }

      return await sdk.new_needs_geo(
        {
          first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
          ...localParam,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),

  homePoint: (latitude: number, longitude: number, need_tags?: string[]) => ({
    queryKey: [{ latitude, longitude, need_tags, forHome: true }],
    async queryFn(ctx) {
      const hasTags = need_tags && need_tags?.length > 0
      const seedValue = getSeedValue()

      let whereConditional:
        | typeof LIVE_NEED
        | { _or: Array<{ data?: any } | typeof LIVE_NEED> } = {
        _or: [
          {
            data: {
              _contains: { tags: need_tags },
            },
            ...LIVE_NEED,
          },
          LIVE_NEED,
        ],
      }

      if (!hasTags) whereConditional = LIVE_NEED

      const localParam = {
        geo: {
          latitude,
          longitude,
          seed: seedValue,
        },
        where: {
          _and: [
            { private: { _neq: true } },
            { urgent: { _eq: false } },
            whereConditional,
          ],
        },
        order_by: [
          {
            line_number: Order_By.Asc,
          },
        ],
      }

      return await sdk.new_needs_geo(
        {
          first: ctx.meta?.pageQuantity || DEFAULT_PAGE_QUANTITY,
          ...localParam,
          ...ctx.pageParam,
        },
        ctx.meta?.headers
      )
    },
  }),
})
export type NeedKeysGeo = inferQueryKeys<typeof needKeysGeo>
